import { cn } from '@/utils/classUtils';
import { useIsOverflown } from '@/utils/overflowUtils';
import { Slot } from '@radix-ui/react-slot';
import { type ClassValue } from 'class-variance-authority/types';
import {
  useImperativeHandle,
  useRef,
  type FC,
  type PropsWithChildren,
  type Ref,
} from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export type EllipsisProps = PropsWithChildren<{
  /**
   * The text element to render as.
   */
  asChild?: boolean;
  className?: ClassValue;
  lineClamp?: number;
  /**
   * Show or not a tooltip when the text is truncated.
   * The tooltip will show the full text.
   * @default false
   */
  hasTooltip?: boolean;
  ref?: Ref<HTMLParagraphElement | null>;
  tooltipSideOffset?: number;
}>;

export const Ellipsis: FC<EllipsisProps> = ({
  asChild,
  children,
  className,
  hasTooltip = false,
  tooltipSideOffset,
  lineClamp,
  ref,
}) => {
  const Comp = asChild ? Slot : 'p';
  const ellipsisRef = useRef<HTMLParagraphElement>(null);
  const isOverflown = useIsOverflown(ellipsisRef, undefined, hasTooltip);

  const innerText = ellipsisRef.current?.textContent;

  useImperativeHandle(ref, () => ellipsisRef.current!);

  const node = (
    <Wrapper
      className={className}
      show={hasTooltip && isOverflown}
      txt={innerText}
      tooltipSideOffset={tooltipSideOffset}
    >
      <Comp
        className={cn(
          'max-w-full min-w-0',
          lineClamp ? 'break-normal [overflow-wrap:anywhere]' : 'break-all',
          !lineClamp && 'truncate',
          className
        )}
        ref={ellipsisRef}
        style={
          lineClamp
            ? {
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: lineClamp,
                lineClamp,
                textOverflow: 'ellipsis',
              }
            : undefined
        }
      >
        {children}
      </Comp>
    </Wrapper>
  );

  if (!hasTooltip) {
    return node;
  }

  return <Tooltip>{node}</Tooltip>;
};

const Wrapper: FC<
  PropsWithChildren<{
    className?: ClassValue;
    txt?: string | null;
    show: boolean;
    tooltipSideOffset?: number;
  }>
> = ({ children, className, txt, show, tooltipSideOffset }) => {
  return show ? (
    <>
      <TooltipTrigger
        asChild
        className={cn('overflow-hidden w-full', className)}
      >
        {children}
      </TooltipTrigger>
      <TooltipContent
        sideOffset={tooltipSideOffset}
        className="break-words text-wrap"
      >
        {txt}
      </TooltipContent>
    </>
  ) : (
    children
  );
};
